import React, { useState } from 'react';
import { Modal, Box, Typography, Button } from '@mui/material';
import { useStyles } from './styles';
import {
  FeedbackFormProps,
  FeedbackFormState,
  QuestionType,
} from './interfaces';
import { feedbackFormData as initialData } from './data';
import {
  canGoToNextStep,
  canGoToPreviousStep,
  isLastStep,
  getCurrentAnswer,
  updateAnswer,
} from './utils';
import {
  HappinessQuestion,
  SelectQuestion,
  RateQuestion,
  TextareaQuestion,
  ThankYouScreen,
} from './components';

const FeedbackForm: React.FC<FeedbackFormProps> = ({
  data = initialData,
  onStepChange,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onAnswerChange,
  onSubmit,
  onClose,
}) => {
  const classes = useStyles();
  const [formState, setFormState] = useState<FeedbackFormState>({
    data,
    currentStep: 0,
    answers: {},
  });
  const [showThankYou, setShowThankYou] = useState(false);

  const handleNextStep = () => {
    if (canGoToNextStep(formState)) {
      if (isLastStep(formState)) {
        console.log('📝 Feedback Form Answers:', formState.answers);
        onSubmit(formState.answers);
        setShowThankYou(true);
      } else {
        const newStep = formState.currentStep + 1;
        setFormState((prev) => ({ ...prev, currentStep: newStep }));
        onStepChange(newStep);
      }
    }
  };

  const handlePreviousStep = () => {
    if (canGoToPreviousStep(formState)) {
      const newStep = formState.currentStep - 1;
      setFormState((prev) => ({ ...prev, currentStep: newStep }));
      onStepChange(newStep);
    }
  };

  const handleAnswerChange = (questionId: string, value: any) => {
    const updatedState = updateAnswer(formState, questionId, value);
    setFormState(updatedState);
    onAnswerChange(questionId, value);
  };

  const currentQuestion = formState.data.steps[formState.currentStep];
  const currentAnswer = getCurrentAnswer(formState);
  const isFirstStep = formState.currentStep === 0;

  const handleCloseThankYou = () => {
    setShowThankYou(false);
    onClose();
  };

  const renderQuestionContent = () => {
    switch (currentQuestion.type) {
      case QuestionType.HAPPINESS:
        return (
          <HappinessQuestion
            question={currentQuestion}
            selectedValue={currentAnswer}
            onSelect={(value: any) =>
              handleAnswerChange(currentQuestion.id, value)
            }
          />
        );
      case QuestionType.SELECT:
        return (
          <SelectQuestion
            question={currentQuestion}
            selectedValues={currentAnswer}
            onSelect={(values: string[]) =>
              handleAnswerChange(currentQuestion.id, values)
            }
          />
        );
      case QuestionType.RATE:
        return (
          <RateQuestion
            question={currentQuestion}
            selectedValue={currentAnswer}
            onSelect={(value: any) =>
              handleAnswerChange(currentQuestion.id, value)
            }
          />
        );
      case QuestionType.TEXTAREA:
        return (
          <TextareaQuestion
            question={currentQuestion}
            selectedValue={currentAnswer}
            onSelect={(value: any) =>
              handleAnswerChange(currentQuestion.id, value)
            }
          />
        );
      default:
        return (
          <Typography
            sx={{
              color: 'white',
              fontFamily: 'Noto Sans',
              opacity: 0.8,
            }}
          >
            Question type: {(currentQuestion as any).type}
            {(currentQuestion as any).subtitle && (
              <Box sx={{ mt: 1, fontSize: '0.9em', opacity: 0.7 }}>
                {(currentQuestion as any).subtitle}
              </Box>
            )}
            {currentAnswer !== undefined && (
              <Box sx={{ mt: 1, fontSize: '0.9em', opacity: 0.9 }}>
                Current answer: {JSON.stringify(currentAnswer)}
              </Box>
            )}
          </Typography>
        );
    }
  };

  return (
    <Modal
      sx={classes.root}
      open={true}
      onClose={onClose}
      disableEscapeKeyDown
      disableAutoFocus
      disableEnforceFocus
      BackdropProps={{
        onClick: () => {},
      }}
    >
      <Box sx={classes.container}>
        {showThankYou ? (
          <ThankYouScreen onClose={handleCloseThankYou} />
        ) : (
          <>
            <Box sx={classes.header}>
              <Typography sx={classes.formTitle}>
                {isFirstStep ? formState.data.formTitle : ''}
              </Typography>
              <Button onClick={onClose} sx={classes.closeButton}>
                <Typography sx={classes.closeButtonText}>✕</Typography>
              </Button>
            </Box>

            <Box sx={classes.questionHeader}>
              <Typography sx={classes.stepIndicator}>
                {formState.currentStep + 1}/{formState.data.steps.length}:{' '}
                {currentQuestion.title}
              </Typography>
              {currentQuestion.subtitle && (
                <Typography sx={classes.questionSubtitle}>
                  {currentQuestion.subtitle}
                </Typography>
              )}
            </Box>

            <Box sx={classes.questionContent}>{renderQuestionContent()}</Box>

            <Box sx={classes.navigationButtons}>
              {!isFirstStep && (
                <Button
                  onClick={handlePreviousStep}
                  sx={classes.previousButton}
                >
                  Previous Question
                </Button>
              )}
              <Button
                onClick={handleNextStep}
                disabled={!canGoToNextStep(formState)}
                sx={{
                  ...classes.nextButton,
                  ...(canGoToNextStep(formState)
                    ? {}
                    : classes.nextButtonDisabled),
                }}
              >
                {isLastStep(formState) ? 'Submit Survey' : 'Next Question'}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default FeedbackForm;
